import { Component, type FC } from "react";
import { connect } from "react-redux";
import { type InjectedFormProps, SubmissionError, reduxForm } from "redux-form";

import { TriggerButton } from "widgets";

import { type OrganismPropertyType } from "../../graphql.js";
import { loadAllOrgPropertyTypes, selectOrgPropertyTypes } from "../../redux/modules/data-editing.js";
import { type ITableColumn } from "../../redux/modules/table-data.js";
import { type IAppState } from "../../redux/reducer.js";
import { DEFAULT_ORG_PROPERTY_GROUP } from "../../settings/settings.js";
import { Dialog } from "./overlays/Dialogs.js";
import { PopOverActionWidget } from "./overlays/index.js";
import ReactSelectField, { ReactSelectReduxFormField } from "./redux-form/ReactSelectField.js";

export interface ISelectPhenotypeTagDialogState {
  options: OrganismPropertyType[];
}

export interface ISelectPhenotypeTagDialogOwnProps {
  close?: () => void;
}

interface ISelectPhenotypeTagDialogAppStateProps {
  existingOrgPropertyTypes: OrganismPropertyType[];
  isLoadingOrgPropertyTypes: boolean;
  initialValues: Partial<any>;
  columns: ITableColumn[];
}

interface ISelectPhenotypeTagDialogDispatchProps {
  selectOrgPropertyTypes: (orgPropertyType: OrganismPropertyType[]) => Promise<OrganismPropertyType>;
  loadAllOrgPropertyTypes: () => void;
}

export interface ISelectPhenotypeTagDialogProps
  extends ISelectPhenotypeTagDialogAppStateProps,
    ISelectPhenotypeTagDialogDispatchProps,
    ISelectPhenotypeTagDialogOwnProps,
    InjectedFormProps<any, any> {}

function mapStateToProps(state: IAppState): ISelectPhenotypeTagDialogAppStateProps {
  return {
    existingOrgPropertyTypes: state.dataEditing.orgPropertyTypes,
    isLoadingOrgPropertyTypes: state.dataEditing.isLoadingOrgPropertyTypes,
    columns: state.dataEditing.columns,
    initialValues: {
      [SelectPhenotypeTagDialog.FIELD_ORG_PROPERTY_TYPE]: null,
    },
  };
}

function mapDispatchToProps(dispatch): ISelectPhenotypeTagDialogDispatchProps {
  return {
    selectOrgPropertyTypes: (orgPropertyType: OrganismPropertyType[]) =>
      dispatch(selectOrgPropertyTypes(orgPropertyType)),
    loadAllOrgPropertyTypes: () => dispatch(loadAllOrgPropertyTypes()),
  };
}

class SelectPhenotypeTagDialogImpl extends Component<ISelectPhenotypeTagDialogProps, ISelectPhenotypeTagDialogState> {
  public static FORM_NAME = "selectPhenotypeTag";

  public static FIELD_ORG_PROPERTY_TYPE = "FIELD_ORG_PROPERTY_TYPE";
  state = { options: [] };

  componentDidMount() {
    this.props.loadAllOrgPropertyTypes();
  }

  componentDidUpdate(prevProps: ISelectPhenotypeTagDialogProps) {
    if (
      this.props.columns !== prevProps.columns ||
      this.props.existingOrgPropertyTypes !== prevProps.existingOrgPropertyTypes ||
      (this.props.isLoadingOrgPropertyTypes !== prevProps.isLoadingOrgPropertyTypes &&
        !this.props.isLoadingOrgPropertyTypes)
    ) {
      this.buildOrgPropertyTypeOptions();
    }
  }

  buildOrgPropertyTypeOptions = () => {
    const visibleColumns = this.props.columns.filter((column) => !column.hidden);
    const options: OrganismPropertyType[] = this.props.existingOrgPropertyTypes.filter(
      (o) => !visibleColumns.find((column) => column.columnId === o.id),
    );
    this.setState({ options });
  };

  selectPhenotypeTag = (values): Promise<any> => {
    return this.handleSubmission(values);
  };

  handleSubmission = (values): Promise<any> => {
    const orgPropertyTypes: OrganismPropertyType[] = values[SelectPhenotypeTagDialogImpl.FIELD_ORG_PROPERTY_TYPE];

    if (!orgPropertyTypes?.length) {
      throw new SubmissionError({
        [SelectPhenotypeTagDialogImpl.FIELD_ORG_PROPERTY_TYPE]: "A selection is required",
      });
    }

    return this.props.selectOrgPropertyTypes(orgPropertyTypes).then(
      (fulfilled) => {
        // make sure the dialog is closed, in production mode, this does not work sometimes without the timeout
        setTimeout(() => {
          this.props.close && this.props.close();
          this.props.reset();
          this.props.destroy();
        }, 100);
        return fulfilled;
      },
      // the following will throw an error that redux-form understands as a submission error,
      // with a general error that will be available from this.props.error
      (rejected) => {
        throw new SubmissionError({ _error: rejected.message });
      },
    );
  };

  render() {
    return (
      <Dialog
        closeFn={this.props.close}
        mainActionButtonProps={{
          isLoading: this.props.submitting || this.props.isLoadingOrgPropertyTypes,
          isDisabled: this.props.submitting,
          children: "Select Phenotype",
          onPress: this.props.handleSubmit(this.selectPhenotypeTag),
        }}
        footerMessage={{ message: this.props.error }}
      >
        <ReactSelectReduxFormField
          name={SelectPhenotypeTagDialogImpl.FIELD_ORG_PROPERTY_TYPE}
          label="Select a phenotype you want to add to the editing table"
          options={this.state.options}
          component={ReactSelectField}
          getOptionValue={(option: OrganismPropertyType) => option.id}
          getOptionLabel={(option: OrganismPropertyType) =>
            `${option.name}${option.group.opgId === DEFAULT_ORG_PROPERTY_GROUP ? "" : ` (${option.group.name})`}`
          }
          isClearable={false}
          isSearchable={true}
          isMulti={true}
        />
      </Dialog>
    );
  }
}

const SelectPhenotypeTagDialog = connect<
  ISelectPhenotypeTagDialogAppStateProps,
  ISelectPhenotypeTagDialogDispatchProps,
  ISelectPhenotypeTagDialogOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    form: SelectPhenotypeTagDialogImpl.FORM_NAME,
  })(SelectPhenotypeTagDialogImpl as any) as any,
);

export const SelectPhenotypeTagButton: FC = () => {
  return (
    <PopOverActionWidget
      title={"Add an existing phenotype, as additional editable phenotype column"}
      actionWidget={({ open }) => (
        <TriggerButton icon={"visibility"} onPress={() => open()} label={"Existing Phenotype"} showLabel />
      )}
    >
      {({ close }) => <SelectPhenotypeTagDialog close={close} />}
    </PopOverActionWidget>
  );
};

export default SelectPhenotypeTagButton;
